import type { AppInfo } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

const UNDEFINED_MOBILE_VERSION = "undefined";
const MOBILE_VERSION_SOURCE = {
  CAPACITOR_APP_GET_INFO: "capacitor_app_getInfo",
  NATIVE_FALLBACK_NO_APP_INFO: "native_fallback_no_app_info",
  WEB_FALLBACK_NO_APP_INFO: "web_fallback_no_app_info",
} as const;

type MobileVersionSource =
  (typeof MOBILE_VERSION_SOURCE)[keyof typeof MOBILE_VERSION_SOURCE];

type MobileVersionTelemetry = {
  mobile_app_id: string;
  mobile_app_name: string;
  mobile_build_number: string;
  mobile_is_native: boolean;
  mobile_platform: string;
  mobile_version: string;
  mobile_version_name: string;
  mobile_version_source: MobileVersionSource;
};

function toTelemetryValue(value: string | null | undefined): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    return UNDEFINED_MOBILE_VERSION;
  }

  return value;
}

function getMobileVersion(buildNumber: string, versionName: string): string {
  if (
    buildNumber === UNDEFINED_MOBILE_VERSION &&
    versionName === UNDEFINED_MOBILE_VERSION
  ) {
    return UNDEFINED_MOBILE_VERSION;
  }

  return `${buildNumber} (${versionName})`;
}

function getMobileVersionSource(
  appInfos: AppInfo | undefined,
  isNativePlatform: boolean
): MobileVersionSource {
  if (appInfos) {
    return MOBILE_VERSION_SOURCE.CAPACITOR_APP_GET_INFO;
  }

  if (isNativePlatform) {
    return MOBILE_VERSION_SOURCE.NATIVE_FALLBACK_NO_APP_INFO;
  }

  return MOBILE_VERSION_SOURCE.WEB_FALLBACK_NO_APP_INFO;
}

function getMobileVersionTelemetry(
  appInfos: AppInfo | undefined = window.appInfos
): MobileVersionTelemetry {
  const isNativePlatform = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();
  const buildNumber = toTelemetryValue(appInfos?.build);
  const versionName = toTelemetryValue(appInfos?.version);
  const appId = toTelemetryValue(appInfos?.id);
  const appName = toTelemetryValue(appInfos?.name);

  return {
    mobile_app_id: appId,
    mobile_app_name: appName,
    mobile_build_number: buildNumber,
    mobile_is_native: isNativePlatform,
    mobile_platform: platform,
    mobile_version: getMobileVersion(buildNumber, versionName),
    mobile_version_name: versionName,
    mobile_version_source: getMobileVersionSource(appInfos, isNativePlatform),
  };
}

export { getMobileVersionTelemetry, UNDEFINED_MOBILE_VERSION };
export type { MobileVersionTelemetry };
